import React, { useState, useContext, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormLabel,
  ToggleButton,
  ToggleButtonGroup,
  alpha,
  IconButton,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import CalculateIcon from '@mui/icons-material/Calculate';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';
import GetAppIcon from '@mui/icons-material/GetApp';
import InventoryIcon from '@mui/icons-material/Inventory';
import { FaTruck, FaBox, FaBoxes, FaPallet, FaShippingFast } from 'react-icons/fa';
import { useProcess } from '../context/ProcessContext';
import { NotificationContext } from '../context/NotificationContext';
import { useActivityLog } from '../context/ActivityLogContext';
import { API_ENDPOINTS } from '../config/api';
import FavoriteButton from '../components/FavoriteButton';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

const StateFreight = () => {
  const theme = useTheme();
  const { addProcess, updateProcess, removeProcess } = useProcess();
  const { addStructuredNotification, showNotification } = useContext(NotificationContext);
  const {
    addActivityProcess,
    updateActivityProcess,
    setLogs: setActivityLogs,
    completeActivityProcess,
    setIntervalRef,
    clearIntervalRef,
  } = useActivityLog();

  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileType, setFileType] = useState('with-dims'); // 'with-dims' or 'item-id-only'
  const [freightType, setFreightType] = useState('rugs-box');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const [processId, setProcessId] = useState(null);
  const [status, setStatus] = useState(null);

  const intervalRef = useRef(null);
  const processIdRef = useRef(null);
  const downloadTriggeredRef = useRef(false);

  // Poll for status when processing
  useEffect(() => {
    if (processId && loading) {
      intervalRef.current = setInterval(() => {
        fetchStatus();
      }, 1000);

      // Store interval in context for cleanup
      setIntervalRef(processId, intervalRef.current);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [loading, processId]);

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const validateFile = (file) => {
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    return validExtensions.includes(fileExtension);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!validateFile(file)) {
        setError('Please upload a valid Excel (.xlsx, .xls) or CSV file');
        return;
      }
      setUploadedFile(file);
      setError('');
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (!validateFile(file)) {
        setError('Please upload a valid Excel (.xlsx, .xls) or CSV file');
        return;
      }
      setUploadedFile(file);
      setError('');
    }
  }, []);

  // Download result file
  const downloadResult = async (downloadProcessId) => {
    if (!downloadProcessId) {
      console.error('No process ID available for download');
      return;
    }

    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${API_ENDPOINTS.STATE_FREIGHT_DOWNLOAD(downloadProcessId)}?t=${Date.now()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `Freight_Calculation_${new Date().toISOString().slice(0, 19).replace(/[:-]/g, '').replace('T', '_')}.xlsx`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        if (document.body.contains(a)) {
          document.body.removeChild(a);
        }
        URL.revokeObjectURL(url);
      }, 1000);

      setResult('Calculation completed successfully! File downloaded.');
      // Notification removed - result opens in new tab
    } catch (err) {
      console.error('Download error:', err);
      setError(`Download failed: ${err.message}`);
    }
  };

  // Fetch processing status
  const fetchStatus = async () => {
    const currentProcessId = processIdRef.current;
    if (!currentProcessId) return;

    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(API_ENDPOINTS.STATE_FREIGHT_STATUS(currentProcessId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        const statusData = data.status;
        setStatus(statusData);

        // Update global activity log
        updateActivityProcess(currentProcessId, {
          progressPercentage: statusData.progressPercentage || 0,
          totalItems: statusData.totalItems || 0,
          processedItems: statusData.processedItems || 0,
        });

        // Update logs in global context
        if (statusData.logs && statusData.logs.length > 0) {
          setActivityLogs(currentProcessId, statusData.logs);
        }

        // Update process context
        updateProcess(currentProcessId, {
          progress: statusData.progressPercentage || 0,
          status: statusData.status
        });

        // Check if processing is complete
        if (statusData.status === 'completed' || statusData.status === 'error') {
          // Clear interval
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          clearIntervalRef(currentProcessId);

          setLoading(false);

          // Complete the activity process
          completeActivityProcess(currentProcessId, statusData.status === 'completed');

          if (statusData.status === 'completed' && !downloadTriggeredRef.current) {
            downloadTriggeredRef.current = true;
            // Download via S3 URL directly and open in new tab
            if (statusData.resultFileUrl) {
              // Download
              const link = document.createElement('a');
              link.href = statusData.resultFileUrl;
              link.download = statusData.resultFileName || 'Freight_Calculation.xlsx';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              // Open in new tab
              window.open(statusData.resultFileUrl, '_blank');
              setResult('Calculation completed successfully! File downloaded and opened in new tab.');
            } else {
              // Fallback to in-memory download endpoint
              downloadResult(currentProcessId);
            }
          } else if (statusData.status === 'error') {
            setError('Freight calculation failed. Check the logs for details.');
          }
        }
      }
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  };

  const handleCalculate = async () => {
    if (!uploadedFile) {
      setError('Please upload a file');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);
    setStatus(null);
    downloadTriggeredRef.current = false;

    const formData = new FormData();
    formData.append('inputFile', uploadedFile);
    formData.append('includesDimensions', fileType === 'with-dims');
    formData.append('freightType', freightType);

    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(API_ENDPOINTS.STATE_FREIGHT_CALCULATE, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        setProcessId(data.processId);
        processIdRef.current = data.processId;

        // Add to global activity log context
        addActivityProcess(data.processId, '48 State Freight Calculation', 'State Freight');

        // Add to process context
        addProcess({
          id: data.processId,
          name: '48 State Freight Calculation',
          progress: 0,
          status: 'processing'
        });
      } else {
        throw new Error(data.message || 'Failed to start freight calculation');
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
      showNotification(`Error: ${err.message}`, 'error');
    }
  };

  const handleDownloadSample = (withDimensions = false) => {
    let sampleContent;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '').substring(0, 15);

    if (withDimensions) {
      sampleContent = `ItemID,Length,Width,Height,UnitWeight,Area
ITEM001,24,18,12,15.5,432
ITEM002,30,20,15,22.0,600
ITEM003,36,24,18,35.5,864
ITEM004,48,30,20,45.0,1440
ITEM005,60,36,24,65.5,2160`;

      const blob = new Blob([sampleContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Freight_Sample_With_Dimensions_${timestamp}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } else {
      sampleContent = 'ItemID\n';

      const blob = new Blob([sampleContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Freight_Sample_ItemID_Only_${timestamp}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }

    showNotification('Sample file downloaded', 'success');
  };

  const removeFile = () => {
    setUploadedFile(null);
  };

  const resetForm = () => {
    setUploadedFile(null);
    setFileType('with-dims');
    setError('');
    setResult(null);
    setProcessId(null);
    processIdRef.current = null;
    downloadTriggeredRef.current = false;
    setStatus(null);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto', position: 'relative' }}>
        <FavoriteButton interfaceName="48 State Freight" category="excel" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          48 STATE FREIGHT
        </Typography>

        <Grid container spacing={3}>
          {/* Main Processing Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Freight Calculation
              </Typography>

              {/* Freight Type Selection */}
              <Card sx={{ mb: 3, bgcolor: alpha(TOOL_COLORS.OTHER.primary, 0.04) }}>
                <CardContent sx={{ pb: 2 }}>
                  <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" sx={{ fontWeight: 600, color: theme.palette.text.primary, mb: 1.5, '&.Mui-focused': { color: theme.palette.text.primary } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FaTruck />
                        <span>Select Freight Type</span>
                      </Box>
                    </FormLabel>
                    <RadioGroup
                      value={freightType}
                      onChange={(e) => setFreightType(e.target.value)}
                    >
                      {/* First Row - 3 options */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <FormControlLabel
                          value="rugs-box"
                          control={<Radio size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.OTHER.primary } }} />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <FaBox size={12} />
                              <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>Rugs (Box)</Typography>
                            </Box>
                          }
                          sx={{ mr: 2 }}
                        />
                        <FormControlLabel
                          value="non-rugs-box"
                          control={<Radio size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.OTHER.primary } }} />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <FaBoxes size={12} />
                              <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>Non-Rugs (Box)</Typography>
                            </Box>
                          }
                          sx={{ mr: 2 }}
                        />
                        <FormControlLabel
                          value="rugs-plastic"
                          control={<Radio size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.OTHER.primary } }} />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <FaBox size={12} />
                              <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>Rugs (Plastic)</Typography>
                            </Box>
                          }
                        />
                      </Box>
                      {/* Second Row - 2 options */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                        <FormControlLabel
                          value="non-rugs-plastic"
                          control={<Radio size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.OTHER.primary } }} />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <FaBoxes size={12} />
                              <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>Non-Rugs (Plastic)</Typography>
                            </Box>
                          }
                          sx={{ mr: 2 }}
                        />
                        <FormControlLabel
                          value="ltl"
                          control={<Radio size="small" sx={{ '&.Mui-checked': { color: TOOL_COLORS.OTHER.primary } }} />}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <FaPallet size={12} />
                              <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>LTL Items</Typography>
                            </Box>
                          }
                        />
                      </Box>
                    </RadioGroup>
                  </FormControl>
                </CardContent>
              </Card>

              {/* File Upload Section */}
              <Card sx={{ mb: 3, bgcolor: theme.palette.background.paper }}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary }}>
                    Upload File
                  </Typography>

                  {/* File Type Toggle */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                      Select file type:
                    </Typography>
                    <ToggleButtonGroup
                      value={fileType}
                      exclusive
                      onChange={(e, newValue) => {
                        if (newValue !== null) {
                          setFileType(newValue);
                        }
                      }}
                      size="small"
                      fullWidth
                      sx={{
                        '& .MuiToggleButton-root': {
                          textTransform: 'none',
                          py: 1,
                          color: theme.palette.text.primary,
                          borderColor: theme.palette.divider,
                          '&.Mui-selected': {
                            bgcolor: alpha(TOOL_COLORS.OTHER.primary, 0.15),
                            color: TOOL_COLORS.OTHER.primary,
                            '&:hover': {
                              bgcolor: alpha(TOOL_COLORS.OTHER.primary, 0.25),
                            }
                          }
                        }
                      }}
                    >
                      <ToggleButton value="with-dims">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <DescriptionIcon fontSize="small" />
                          <span>With Dimensions</span>
                        </Box>
                      </ToggleButton>
                      <ToggleButton value="item-id-only">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <InventoryIcon fontSize="small" />
                          <span>ItemID Only</span>
                        </Box>
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: theme.palette.text.secondary }}>
                      {fileType === 'with-dims'
                        ? 'File should contain: ItemID, Length, Width, Height, UnitWeight, Area'
                        : 'File should contain: ItemID only (dimensions fetched from AWS)'}
                    </Typography>
                  </Box>

                  {/* Drag & Drop Zone */}
                  <Box
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    sx={{
                      border: `2px dashed ${isDragOver ? TOOL_COLORS.OTHER.primary : uploadedFile ? '#4CAF50' : theme.palette.divider}`,
                      borderRadius: 2,
                      p: 4,
                      textAlign: 'center',
                      bgcolor: isDragOver
                        ? alpha(theme.palette.warning.main, 0.08)
                        : uploadedFile
                          ? (theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.06)')
                          : alpha(theme.palette.action.hover, 0.02),
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        borderColor: TOOL_COLORS.OTHER.primary,
                        bgcolor: alpha(theme.palette.warning.main, 0.04),
                      }
                    }}
                    onClick={() => document.getElementById('file-upload-input').click()}
                  >
                    <input
                      id="file-upload-input"
                      type="file"
                      hidden
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileUpload}
                    />

                    {uploadedFile ? (
                      <Box>
                        <CheckCircleIcon sx={{ fontSize: 48, color: '#4CAF50', mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                          1 file selected
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                          Click to replace
                        </Typography>
                      </Box>
                    ) : (
                      <Box>
                        <CloudUploadIcon sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                          Drag & drop your file here
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                          or click to browse
                        </Typography>
                        <Typography variant="caption" sx={{ color: theme.palette.text.disabled, display: 'block', mt: 1 }}>
                          Supports: .xlsx, .xls, .csv
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* File Bar */}
                  {uploadedFile && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, mt: 1.5, bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                        <DescriptionIcon sx={{ color: TOOL_COLORS.OTHER.primary, fontSize: 20 }} />
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{uploadedFile.name}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>({(uploadedFile.size / 1024).toFixed(1)} KB)</Typography>
                      </Box>
                      <IconButton size="small" onClick={() => removeFile()} sx={{ color: theme.palette.error.main }}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}

                  {/* Template Downloads */}
                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={() => handleDownloadSample(true)}
                      startIcon={<GetAppIcon sx={{ fontSize: '18px !important' }} />}
                      sx={{
                        borderColor: alpha(theme.palette.text.primary, 0.2),
                        color: alpha(theme.palette.text.primary, 0.7),
                        bgcolor: 'transparent',
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2.5,
                        py: 0.8,
                        '&:hover': {
                          borderColor: alpha(theme.palette.text.primary, 0.4),
                          color: theme.palette.text.primary,
                          bgcolor: alpha(theme.palette.text.primary, 0.05),
                        },
                        '&:active': {
                          transform: 'scale(0.98)',
                        },
                      }}
                    >
                      Download DIMS Template
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => handleDownloadSample(false)}
                      startIcon={<GetAppIcon sx={{ fontSize: '18px !important' }} />}
                      sx={{
                        borderColor: alpha(theme.palette.text.primary, 0.2),
                        color: alpha(theme.palette.text.primary, 0.7),
                        bgcolor: 'transparent',
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2.5,
                        py: 0.8,
                        '&:hover': {
                          borderColor: alpha(theme.palette.text.primary, 0.4),
                          color: theme.palette.text.primary,
                          bgcolor: alpha(theme.palette.text.primary, 0.05),
                        },
                        '&:active': {
                          transform: 'scale(0.98)',
                        },
                      }}
                    >
                      Download ItemID Template
                    </Button>
                  </Box>
                </CardContent>
              </Card>

              {/* Calculate Button */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleCalculate}
                  disabled={loading || !uploadedFile}
                  startIcon={loading ? <CircularProgress size={20} /> : <CalculateIcon />}
                  sx={{
                    bgcolor: TOOL_COLORS.OTHER.primary,
                    '&:hover': {
                      bgcolor: TOOL_COLORS.OTHER.hover,
                    },
                    px: 4
                  }}
                >
                  {loading ? 'Calculating...' : 'Calculate Freight'}
                </Button>

                {(uploadedFile || result) && (
                  <Button
                    variant="outlined"
                    onClick={resetForm}
                    disabled={loading}
                    sx={{
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      '&:hover': {
                        borderColor: theme.palette.text.secondary,
                        bgcolor: alpha(theme.palette.text.primary, 0.05),
                      }
                    }}
                  >
                    Clear
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Status & Information Section */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Status & Information
              </Typography>

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {error}
                </Alert>
              )}

              {/* Success Alert */}
              {result && (
                <Alert severity="success" sx={{ mb: 3 }}>
                  {result}
                </Alert>
              )}

              {/* Info Cards */}
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Key Information:
              </Typography>
              <List dense disablePadding>
                <ListItem sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <InfoIcon color="info" sx={{ fontSize: 18 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Constraints for Non LTL Items"
                    secondary="Max: 150 LB, 108″ L, 165″ Girth"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <FaPallet size={16} />
                  </ListItemIcon>
                  <ListItemText
                    primary="LTL Items (Metropolitan, 48 states)"
                    secondary="$3.29 per cu ft (min $154.07) + 47.5% fuel + 10% handling, rounded up to the next $5. A boxed item ships LTL when any of its content items is LTL, priced on the total volume of all contents; otherwise its freight is the sum of each content item's own freight."
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <FaShippingFast size={16} />
                  </ListItemIcon>
                  <ListItemText
                    primary="SmartPost"
                    secondary="Auto-detects packages under 10 LB"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <CalculateIcon color="warning" sx={{ fontSize: 18 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Surcharges"
                    secondary="Includes AHS, Oversize, Residential & DAS"
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              </List>

              <Divider sx={{ my: 3 }} />

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: theme.palette.text.primary }}>
                Instructions:
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.8 }}>
                <strong>1.</strong> Select freight type<br />
                <strong>2.</strong> Choose file type (with/without dims)<br />
                <strong>3.</strong> Drag & drop or click to upload<br />
                <strong>4.</strong> Click Calculate to process<br />
                <strong>5.</strong> Excel report downloads automatically
              </Typography>

            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default StateFreight;
